import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckIcon from "@material-ui/icons/Check";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PublicIcon from "@material-ui/icons/Public";
import ExtensionIcon from "@material-ui/icons/Extension";
import MapIcon from "@material-ui/icons/Map";
import LanguageIcon from "@material-ui/icons/Language";
import FlagIcon from "@material-ui/icons/Flag";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import "./home-landing.css";
import "./progress-showcase.css";

/*
 * "All in one place": Activity Overview, Student Tracker, Fee Collection and Admissions,
 * redrawn as small live demos (sample data only). The active tab's progress bar times
 * the tour: when its animation ends the next tab opens.
 */

const STAGES = [
  { key: "P", label: "Present" },
  { key: "WA", label: "Work with assist" },
  { key: "WI", label: "Work independent" },
  { key: "M", label: "Master" },
];

const Badge = ({ stage, delay = 0 }) => (
  <span className={`lhp-badge lhp-badge--${stage}`} style={{ "--d": `${delay}ms` }}>
    {stage}
  </span>
);

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// True once the section has been on screen, so numbers start counting when seen.
const StartedContext = createContext(false);

/** A number that counts up from 0 once the section is on screen. */
const CountUp = ({ to, format = (n) => Math.round(n).toLocaleString("en-IN"), duration = 1400 }) => {
  const started = useContext(StartedContext);
  const [value, setValue] = useState(() => (reducedMotion() ? to : 0));
  useEffect(() => {
    if (!started || reducedMotion()) return undefined;
    let frame;
    const begin = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - begin) / duration);
      setValue(to * (1 - (1 - t) ** 3));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [started, to, duration]);
  return <>{format(value)}</>;
};

/** Horizontal bar that grows to `pct`, after `delay` ms. */
const Bar = ({ pct, color, delay = 0 }) => (
  <span className="lhp-bar">
    <span className="lhp-bar__fill" style={{ width: `${pct}%`, background: color, "--d": `${delay}ms` }} />
  </span>
);

/* ---------- Screen 1: Activity Overview ---------- */

const OV_STUDENTS = [
  ["Aarav", "#6366f1"],
  ["Diya", "#ec4899"],
  ["Kabir", "#f97316"],
  ["Meera", "#10b981"],
  ["Rohan", "#06b6d4"],
  ["Anaya", "#a855f7"],
];
const OV_ROWS = ["Sand Globe", "Continent Globe", "Globe Map", "World Map", "Ocean Names"];
const OV_MARKS = {
  "0-0": "M", "0-2": "WI", "0-3": "M", "0-5": "WA",
  "1-0": "WI", "1-1": "M", "1-3": "WA", "1-4": "P",
  "2-1": "WA", "2-2": "P", "2-3": "M", "2-5": "WI",
  "3-0": "P", "3-4": "WA", "3-5": "M",
  "4-1": "WI", "4-3": "WI", "4-4": "M",
};
const OV_ORDER = Object.keys(OV_MARKS);
const marksOf = (col) => OV_ROWS.map((_, r) => OV_MARKS[`${r}-${col}`]).filter(Boolean);

const Donut = ({ pct }) => {
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <span className="lhp-donut">
      <svg viewBox="0 0 84 84" aria-hidden="true">
        <defs>
          <linearGradient id="lhpDonutGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        <circle cx="42" cy="42" r={r} className="lhp-donut__track" />
        <circle
          cx="42"
          cy="42"
          r={r}
          className="lhp-donut__value"
          style={{ strokeDasharray: c, "--from": c, strokeDashoffset: c * (1 - pct / 100) }}
        />
      </svg>
      <b>
        <CountUp to={pct} format={(n) => `${Math.round(n)}%`} />
      </b>
    </span>
  );
};

const OverviewScreen = () => (
  <div className="lhp-ov">
    <div className="lhp-kpis">
      <div className="lhp-kpi" style={{ "--k": "#4f46e5" }}>
        <PeopleOutlineIcon fontSize="small" />
        <span>
          <small>Total students</small>
          <b>
            <CountUp to={12} />
          </b>
        </span>
      </div>
      <div className="lhp-kpi" style={{ "--k": "#ec4899" }}>
        <AssignmentOutlinedIcon fontSize="small" />
        <span>
          <small>Activities</small>
          <b>
            <CountUp to={18} />
          </b>
        </span>
      </div>
      <div className="lhp-kpi" style={{ "--k": "#10b981" }}>
        <TrendingUpIcon fontSize="small" />
        <span>
          <small>Overall progress</small>
          <b>
            <CountUp to={76} format={(n) => `${Math.round(n)}%`} />
          </b>
        </span>
      </div>
    </div>

    <div className="lhp-ov__main">
      <aside className="lhp-ov__side">
        <span className="lhp-ov__title">Classroom insight</span>
        <Donut pct={76} />
        <ul className="lhp-ov__split">
          <li style={{ "--k": "#10b981" }}>
            Completed <b>52%</b>
          </li>
          <li style={{ "--k": "#f59e0b" }}>
            In progress <b>24%</b>
          </li>
          <li style={{ "--k": "#cbd0de" }}>
            Not started <b>24%</b>
          </li>
        </ul>
      </aside>

      <div className="lhp-ov__table">
        <span className="lhp-ov__corner">
          Geography <small>Showing 6 of 12</small>
        </span>
        {OV_STUDENTS.map(([name, color]) => (
          <span className="lhp-ov__kid" key={name}>
            <i style={{ background: color }}>{name[0]}</i>
            {name}
          </span>
        ))}
        {OV_ROWS.map((row, r) => (
          <React.Fragment key={row}>
            <span className="lhp-ov__row">{row}</span>
            {OV_STUDENTS.map(([name], c) => {
              const id = `${r}-${c}`;
              const mark = OV_MARKS[id];
              return (
                <span className="lhp-cell" key={name}>
                  {mark && <Badge stage={mark} delay={500 + OV_ORDER.indexOf(id) * 240} />}
                </span>
              );
            })}
          </React.Fragment>
        ))}
        <span className="lhp-ov__foot">Completion</span>
        {OV_STUDENTS.map(([name], c) => (
          <span className="lhp-ov__num" key={name}>
            {Math.round((marksOf(c).length / OV_ROWS.length) * 100)}%
          </span>
        ))}
        <span className="lhp-ov__foot lhp-ov__foot--good">Independent</span>
        {OV_STUDENTS.map(([name], c) => (
          <span className="lhp-ov__num lhp-ov__num--good" key={name}>
            {marksOf(c).filter((m) => m === "WI" || m === "M").length}
          </span>
        ))}
        <span className="lhp-ov__foot lhp-ov__foot--warn">Needs support</span>
        {OV_STUDENTS.map(([name], c) => (
          <span className="lhp-ov__num lhp-ov__num--warn" key={name}>
            {marksOf(c).filter((m) => m === "P" || m === "WA").length}
          </span>
        ))}
      </div>
    </div>

    <div className="lhp-legend">
      {STAGES.map(({ key, label }) => (
        <span key={key}>
          <span className={`lhp-badge lhp-badge--${key} lhp-badge--still`}>{key}</span>
          {label}
        </span>
      ))}
    </div>
  </div>
);

/* ---------- Screen 2: Student Tracker ---------- */

const TRACK_STUDENTS = [
  ["Aarav", "AA"],
  ["Diya", "DI"],
  ["Kabir", "KA"],
  ["Meera", "ME"],
  ["Rohan", "RO"],
];
const TRACK_WORKS = [
  { name: "Sandpaper Globe", icon: PublicIcon, tone: "#7c3aed", level: 2 },
  { name: "Continent Globe", icon: LanguageIcon, tone: "#ea580c", level: 1 },
  { name: "World Puzzle Map", icon: ExtensionIcon, tone: "#059669", level: 4 },
  { name: "Continent Puzzle Map", icon: MapIcon, tone: "#0284c7", level: 0 },
  { name: "Country Puzzle Map", icon: FlagIcon, tone: "#db2777", level: 3 },
];

const TrackerScreen = () => {
  let step = 0;
  return (
    <div className="lhp-track">
      <div className="lhp-track__rail">
        <span className="lhp-track__railhead">Students</span>
        {TRACK_STUDENTS.map(([name, short]) => (
          <span className={`lhp-track__kid ${name === "Diya" ? "is-on" : ""}`} key={name}>
            <i>{short}</i>
            {name}
          </span>
        ))}
      </div>
      <div className="lhp-track__main">
        <div className="lhp-track__head">
          <i>DI</i>
          <div>
            <b>Diya</b> <small>5y 11m</small>
            <span>Geography › Culture</span>
          </div>
        </div>
        {TRACK_WORKS.map(({ name, icon: Icon, tone, level }) => (
          <div className="lhp-work" key={name}>
            <span className="lhp-work__icon" style={{ color: tone, background: `${tone}1f` }}>
              <Icon fontSize="small" />
            </span>
            <span className="lhp-work__name">
              {name}
              <small>Geography</small>
            </span>
            <span className="lhp-steps">
              {STAGES.map(({ key, label }, i) => {
                const on = i < level;
                const delay = on ? 500 + step++ * 420 : 0;
                return (
                  <span className="lhp-step" key={key}>
                    <span className="lhp-step__dot">{on && <Badge stage={key} delay={delay} />}</span>
                    <small>{label}</small>
                  </span>
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Screen 3: Fee Collection ---------- */

const FEE_TILES = [
  { label: "Offline", note: "Cash, card, net banking", value: 390000, tone: "#10b981" },
  { label: "Online", note: "UPI, cards, net banking", value: 510000, tone: "#0ea5e9" },
  { label: "Cheques", note: "Received today", value: 69000, tone: "#f59e0b" },
];
const FEE_TOTAL = FEE_TILES.reduce((sum, t) => sum + t.value, 0);
const FEE_TABLES = [
  {
    title: "Offline collection",
    tone: "#10b981",
    rows: [["Cash", 250000], ["Net banking", 90000], ["Card", 50000]],
  },
  {
    title: "Online collection",
    tone: "#0ea5e9",
    rows: [["UPI", 300000], ["Debit card", 90000], ["Credit card", 70000], ["Net banking", 50000]],
  },
];

const FeeScreen = () => (
  <div className="lhp-fee">
    <div className="lhp-fee__tiles">
      {FEE_TILES.map(({ label, note, value, tone }) => (
        <div className="lhp-tile" style={{ "--k": tone }} key={label}>
          <small>{label}</small>
          <b>
            <CountUp to={value} format={inr} />
          </b>
          <span>{note}</span>
        </div>
      ))}
      <div className="lhp-tile lhp-tile--total">
        <small>Total today</small>
        <b>
          <CountUp to={FEE_TOTAL} format={inr} />
        </b>
        <span>Consolidated</span>
      </div>
    </div>

    <div className="lhp-fee__tables">
      {FEE_TABLES.map(({ title, tone, rows }, t) => (
        <div className="lhp-panel" key={title}>
          <span className="lhp-panel__title">{title}</span>
          {rows.map(([mode, amount], i) => (
            <div className="lhp-mode" key={mode}>
              <span className="lhp-mode__top">
                {mode} <b>{inr(amount)}</b>
              </span>
              <Bar pct={(amount / rows[0][1]) * 100} color={tone} delay={700 + t * 500 + i * 220} />
            </div>
          ))}
        </div>
      ))}
    </div>

    <div className="lhp-share">
      <span className="lhp-panel__title">Consolidated for the day</span>
      <span className="lhp-share__bar">
        {FEE_TILES.map(({ label, value, tone }, i) => (
          <span key={label} style={{ width: `${(value / FEE_TOTAL) * 100}%`, background: tone, "--d": `${1800 + i * 300}ms` }} />
        ))}
      </span>
      <span className="lhp-share__keys">
        {FEE_TILES.map(({ label, value, tone }) => (
          <span key={label} style={{ "--k": tone }}>
            {label} <b>{Math.round((value / FEE_TOTAL) * 100)}%</b>
          </span>
        ))}
      </span>
    </div>

    <div className="lhp-cheque">
      <span className="lhp-cheque__label">Cheque 234980 · SBI, Mallapur</span>
      <b>{inr(69000)}</b>
      <span className="lhp-cheque__status" aria-hidden="true">
        <i>Pending</i>
        <i className="is-done">Credited</i>
      </span>
    </div>
  </div>
);

/* ---------- Screen 4: Admissions ---------- */

const ADM_TILES = [
  { label: "New enquiries", value: 24, tone: "#f97316" },
  { label: "Tours", value: 16, tone: "#a855f7" },
  { label: "Admissions", value: 9, tone: "#10b981" },
  { label: "Lost", value: 3, tone: "#f43f5e" },
  { label: "Conversion", value: 38, tone: "#0ea5e9", percent: true },
  { label: "Follow-ups", value: 57, tone: "#eab308" },
];
const ADM_SOURCES = [
  ["Walk-in", 9, "#f97316"],
  ["School Website", 6, "#ec4899"],
  ["Referral", 4, "#a855f7"],
  ["Social Media", 3, "#0ea5e9"],
  ["Sibling", 2, "#10b981"],
];
const ADM_PIPELINE = [
  ["New", 4, "#f97316"],
  ["Contacted", 3, "#fb923c"],
  ["Interested", 2, "#eab308"],
  ["Tour Scheduled", 1, "#a855f7"],
  ["Tour Done", 1, "#8b5cf6"],
  ["Application Submitted", 1, "#0ea5e9"],
  ["Admitted", 9, "#10b981"],
  ["Lost", 3, "#f43f5e"],
];

const ADM_TODAY = [
  { name: "Priya Nair", what: "· walk-in, school tour at 11 AM", status: "Tour Scheduled", tone: "#7c3aed" },
  { name: "Sameer Khan", what: "· call back about Primary", status: "Interested", tone: "#a16207" },
];

const AdmissionsScreen = () => (
  <div className="lhp-adm">
    <div className="lhp-adm__tiles">
      {ADM_TILES.map(({ label, value, tone, percent }) => (
        <div className="lhp-tile lhp-tile--small" style={{ "--k": tone }} key={label}>
          <small>{label}</small>
          <b>
            <CountUp to={value} format={percent ? (n) => `${Math.round(n)}%` : undefined} />
          </b>
        </div>
      ))}
    </div>
    <div className="lhp-adm__panels">
      <div className="lhp-panel">
        <span className="lhp-panel__title">Enquiries by source</span>
        {ADM_SOURCES.map(([name, n, color], i) => (
          <div className="lhp-rowbar" key={name}>
            <span>{name}</span>
            <Bar pct={(n / ADM_SOURCES[0][1]) * 100} color={color} delay={600 + i * 200} />
            <b>{n}</b>
          </div>
        ))}
      </div>
      <div className="lhp-panel">
        <span className="lhp-panel__title">Pipeline</span>
        {ADM_PIPELINE.map(([name, n, color], i) => (
          <div className="lhp-rowbar" key={name}>
            <span>{name}</span>
            <Bar pct={(n / 9) * 100} color={color} delay={1000 + i * 180} />
            <b>{n}</b>
          </div>
        ))}
      </div>
    </div>
    <div className="lhp-panel lhp-adm__today">
      <span className="lhp-panel__title">Today's follow-ups</span>
      {ADM_TODAY.map(({ name, what, status, tone }, i) => (
        <div className="lhp-followup" key={name} style={{ "--d": `${2800 + i * 500}ms` }}>
          <i style={{ background: tone }}>{name[0]}</i>
          <span>
            <b>{name}</b> {what}
          </span>
          <em style={{ color: tone, background: `${tone}1f` }}>{status}</em>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- The tour ---------- */

const SCREENS = [
  {
    id: "overview",
    tab: "Activity Overview",
    title: "See how the whole class is doing.",
    text: "Every child and activity on one page, with class progress, completion and who needs support next, so guides know exactly where to spend their time.",
    points: ["Class progress at a glance", "Who needs support, child by child", "Simple marks: P, WA, WI and M"],
    window: "Activity Progress Overview",
    toast: { stage: "M", text: "Rohan mastered Ocean Names" },
    a1: "#4f46e5",
    a2: "#06b6d4",
    Screen: OverviewScreen,
  },
  {
    id: "tracker",
    tab: "Student Tracker",
    title: "One child's journey, work by work.",
    text: "Open a child and an area of work to see each exercise move from its first presentation all the way to mastery.",
    points: ["Browse by student or by work", "Filter by classroom, section and area", "Find any exercise with search"],
    window: "Student Tracker · Diya",
    toast: { stage: "WI", text: "Diya works independently" },
    a1: "#7c3aed",
    a2: "#ec4899",
    Screen: TrackerScreen,
  },
  {
    id: "fees",
    tab: "Fee Collection",
    title: "Online and office fees, in one report.",
    text: "Parents pay online from the app or web. The office collects cash, card, net banking and cheques. Every payment gets a receipt and lands in the same day report.",
    points: ["Auto-numbered receipts, amount in words", "Day report by payment mode", "Cheques tracked until credited or returned"],
    window: "Fee Collection · Day report",
    toast: { icon: ReceiptOutlinedIcon, tone: "#059669", text: "Receipt RCPT-00124 created" },
    a1: "#059669",
    a2: "#22d3ee",
    Screen: FeeScreen,
  },
  {
    id: "admissions",
    tab: "Admissions",
    title: "Every enquiry, from first call to admission.",
    text: "Fill in the enquiry form at the front desk for every walk-in or phone call, follow up on time, and see which sources turn into admissions.",
    points: ["Quick add or the full enquiry form", "Today's follow-ups, so no family is missed", "Admitted enquiries pre-fill student registration"],
    window: "Admissions dashboard · This month",
    toast: { icon: PersonAddOutlinedIcon, tone: "#ea580c", text: "Enquiry marked Admitted" },
    a1: "#f97316",
    a2: "#f43f5e",
    Screen: AdmissionsScreen,
  },
];

const ProgressShowcase = () => {
  const history = useHistory();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [inView, setInView] = useState(false);
  const [started, setStarted] = useState(false);
  const rootRef = useRef(null);

  // The tour only runs while the section is on screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      setStarted(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const next = () => setActive((a) => (a + 1) % SCREENS.length);
  const onTabKey = (e) => {
    const move = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!move) return;
    e.preventDefault();
    const to = (active + move + SCREENS.length) % SCREENS.length;
    setActive(to);
    const buttons = rootRef.current?.querySelectorAll('[role="tab"]');
    if (buttons && buttons[to]) buttons[to].focus();
  };

  const running = inView && !paused && !hovering;
  const { window: windowTitle, toast, Screen, id, a1, a2 } = SCREENS[active];
  const ToastIcon = toast.icon;

  return (
    <section className="lh-sec lhp" aria-labelledby="lhp-title" ref={rootRef} style={{ "--a1": a1, "--a2": a2 }}>
      <div className="lh-wrap">
        <div className={`lhp-band ${running ? "" : "lhp-frozen"}`}>
          <header className="lhp-head">
            <p className="lhp-eyebrow">One bright workspace</p>
            <h2 className="lh-h2" id="lhp-title">
              From first enquiry to final mastery, <span className="lhp-accent">all in one place.</span>
            </h2>
            <p className="lhp-lead">
              Follow each child's learning, collect fees online and at the office, and never lose an admission enquiry.
              Here is what that looks like, live.
            </p>
          </header>

          <div className="lhp-body">
            <div className="lhp-tabs">
              <div role="tablist" aria-label="Montestory screens" aria-orientation="vertical" className="lhp-tablist">
                {SCREENS.map((s, i) => {
                  const on = i === active;
                  return (
                    <div className={`lhp-tabcard ${on ? "is-on" : ""}`} key={s.id} style={{ "--t1": s.a1, "--t2": s.a2 }}>
                      <button
                        type="button"
                        role="tab"
                        id={`lhp-tab-${s.id}`}
                        aria-selected={on}
                        aria-controls="lhp-panel"
                        tabIndex={on ? 0 : -1}
                        className="lhp-tab"
                        onClick={() => setActive(i)}
                        onKeyDown={onTabKey}
                      >
                        <span className="lhp-tab__n">0{i + 1}</span>
                        <span className="lhp-tab__name">{s.tab}</span>
                      </button>
                      {on && (
                        <div className="lhp-tab__more">
                          <h3>{s.title}</h3>
                          <p>{s.text}</p>
                          <ul>
                            {s.points.map((p) => (
                              <li key={p}>
                                <CheckIcon fontSize="inherit" /> {p}
                              </li>
                            ))}
                          </ul>
                          <span className="lhp-tab__track" aria-hidden="true">
                            <span className="lhp-tab__bar" onAnimationEnd={next} />
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="lhp-actions">
                <button type="button" className="lh-btn lh-btn--primary lhp-cta" onClick={() => history.push("/montessori/schoolregistration")}>
                  Start your free trial <ArrowForwardIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="lhp-pause"
                  onClick={() => setPaused((p) => !p)}
                  aria-label={paused ? "Play the tour" : "Pause the tour"}
                  title={paused ? "Play the tour" : "Pause the tour"}
                >
                  {paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                </button>
              </div>
            </div>

            <div
              className="lhp-stage"
              id="lhp-panel"
              role="tabpanel"
              aria-labelledby={`lhp-tab-${id}`}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
            >
              {/* One colour layer per screen, cross-faded so the backdrop glides between themes. */}
              {SCREENS.map((s) => (
                <span
                  key={s.id}
                  className={`lhp-stage__bg ${s.id === id ? "is-on" : ""}`}
                  style={{ background: `linear-gradient(135deg, ${s.a1}, ${s.a2})` }}
                  aria-hidden="true"
                />
              ))}
              <span className="lhp-orb lhp-orb--one" aria-hidden="true" />
              <span className="lhp-orb lhp-orb--two" aria-hidden="true" />

              {/* Remounted per tab so its animation replays from the start. */}
              <StartedContext.Provider value={started}>
                <div className="lhp-window" key={id} aria-hidden="true">
                  <div className="lhp-window__chrome">
                    <i />
                    <i />
                    <i />
                    <span>{windowTitle}</span>
                  </div>
                  <div className="lhp-window__body">
                    <Screen />
                  </div>
                  <div className="lhp-toast">
                    {ToastIcon ? (
                      <span className="lhp-toast__icon" style={{ background: toast.tone }}>
                        <ToastIcon style={{ fontSize: 15 }} />
                      </span>
                    ) : (
                      <Badge stage={toast.stage} />
                    )}
                    {toast.text}
                  </div>
                </div>
              </StartedContext.Provider>
            </div>
          </div>
          <p className="lhp-note">Sample names and figures shown for illustration.</p>
        </div>
      </div>
    </section>
  );
};

export default ProgressShowcase;
